import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Mail, ExternalLink } from 'lucide-react';
import { SiInstagram, SiLinkedin } from 'react-icons/si';
import { SOCIAL_LINKS } from '@/lib/socials';
const NAV_ITEMS = [{
  label: 'Music',
  id: 'music'
}, {
  label: 'Services',
  id: 'services'
}, {
  label: 'Experience',
  id: 'experience'
}, {
  label: 'About',
  id: 'about'
}, {
  label: 'Contact',
  id: 'contact'
}];

// Reel Crafter SVG icon (film strip shape)
function ReelCrafterIcon({
  className = 'w-4 h-4'
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3ZM8 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm4-4a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm6 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0-10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
    </svg>;
}
export const SOCIALS = [{
  label: 'Instagram',
  href: SOCIAL_LINKS.instagram,
  icon: <SiInstagram className="w-4 h-4" />,
  color: 'hover:text-pink-400'
}, {
  label: 'LinkedIn',
  href: SOCIAL_LINKS.linkedin,
  icon: <SiLinkedin className="w-4 h-4" />,
  color: 'hover:text-blue-400'
}, {
  label: 'Reel Crafter',
  href: SOCIAL_LINKS.reelcrafter,
  icon: <ReelCrafterIcon />,
  color: 'hover:text-primary'
}];
function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth'
  });
}
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-card/80 backdrop-blur-md border-b border-border shadow-lg' : 'bg-transparent'}`}>
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => scrollToSection('hero')} className="flex items-center gap-3 group" aria-label="Go to top">
          
          <span className="font-bold text-lg text-foreground tracking-tight hidden sm:block">
            K.S. Score & Sound
          </span>
        </button>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map(item => <li key={item.id}>
              <button onClick={() => scrollToSection(item.id)} className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-200">
                {item.label}
              </button>
            </li>)}

          {/* Social icons in nav */}
          <li className="flex items-center gap-1 ml-2 pl-2 border-l border-border">
            {SOCIALS.map(s => <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className={`p-2 rounded-lg text-muted-foreground transition-colors duration-200 ${s.color}`}>
                {s.icon}
              </a>)}
          </li>

          <li>
            <button onClick={() => scrollToSection('contact')} className="ml-2 px-5 py-2 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-all duration-200 shadow-md">
              Hire Me
            </button>
          </li>
        </ul>

        {/* Mobile Hamburger */}
        <button className="md:hidden p-2 rounded-lg hover:bg-muted/40 transition-colors" onClick={() => setMobileOpen(v => !v)} aria-label="Toggle menu">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} className="md:hidden bg-card/95 backdrop-blur-md border-b border-border px-6 pb-4">
            <ul className="flex flex-col gap-1 pt-2">
              {NAV_ITEMS.map(item => <li key={item.id}>
                  <button onClick={() => {
              scrollToSection(item.id);
              setMobileOpen(false);
            }} className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all">
                    {item.label}
                  </button>
                </li>)}
              <li>
                <button onClick={() => {
              scrollToSection('contact');
              setMobileOpen(false);
            }} className="w-full mt-1 px-4 py-3 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-all text-left">
                  Hire Me
                </button>
              </li>
            </ul>
            {/* Social row in mobile menu */}
            <div className="flex gap-3 pt-3 mt-1 border-t border-border">
              {SOCIALS.map(s => <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className={`flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors ${s.color}`}>
                  {s.icon}
                  {s.label}
                </a>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}
export function Footer() {
  return <footer className="border-t border-border bg-card/40 pt-12 pb-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Top row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src="/images/logo.png" alt="K.S. Score & Sound" className="h-10 w-auto" />
              <span className="text-sm font-bold text-foreground">K.S. Score & Sound</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
              Cinematic music composition & sound design for film, television, games, and media —
              by Konstantin Solopov.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <p className="text-xs font-mono text-primary uppercase tracking-widest mb-4">Navigate</p>
            <ul className="space-y-2">
              {NAV_ITEMS.map(item => <li key={item.id}>
                  <button onClick={() => scrollToSection(item.id)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {item.label}
                  </button>
                </li>)}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <p className="text-xs font-mono text-primary uppercase tracking-widest mb-4">Connect</p>
            <ul className="space-y-3">
              <li>
                <a href={SOCIAL_LINKS.email} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  ks.score.sound@gmail.com
                </a>
              </li>
              {SOCIALS.map(s => <li key={s.label}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 text-xs text-muted-foreground transition-colors ${s.color}`}>
                    <span className="flex-shrink-0">{s.icon}</span>
                    {s.label}
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Konstantin Solopov. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            {SOCIALS.map(s => <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className={`p-1.5 rounded-lg text-muted-foreground transition-colors ${s.color}`}>
                {s.icon}
              </a>)}
          </div>
        </div>
      </div>
    </footer>;
}